/**
 * LanguageSwitcher — 语言切换组件
 * 
 * 功能：
 *   - 下拉选择语言（中/英/日）
 *   - 保存用户偏好到 localStorage
 *   - 切换后实时更新界面语言
 * 
 * 使用方式：
 *   <LanguageSwitcher /> — 默认样式（下拉菜单）
 *   <LanguageSwitcher compact /> — 紧凑样式（仅图标+语言代码）
 */

import { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SUPPORTED_LANGUAGES,
  changeLanguage,
  getCurrentLanguage,
} from '@/i18n';

/* ── Spring 动画 ── */
const spring = { type: 'spring', bounce: 0.15, duration: 0.4 };

const LanguageSwitcher = ({ compact = false }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const menuRef = useRef(null);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 监听 i18n 语言变化
  useEffect(() => {
    const handleLangChanged = (lng) => setCurrentLang(lng);
    i18n.on('languageChanged', handleLangChanged);
    return () => i18n.off('languageChanged', handleLangChanged);
  }, [i18n]);

  const handleSelect = (langCode) => {
    changeLanguage(langCode);
    setCurrentLang(langCode);
    setOpen(false);
  };

  const currentLangInfo = SUPPORTED_LANGUAGES.find((l) => l.code === currentLang);

  if (compact) {
    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1 px-2 py-1 rounded-lg text-[12px] font-medium transition-colors duration-200"
          style={{
            color: 'rgba(200, 220, 240, 0.7)',
            background: 'rgba(100, 160, 200, 0.06)',
          }}
          title={t('language.switch')}
        >
          <Globe className="w-3.5 h-3.5" />
          <span>{currentLang?.code?.split('-')[0]?.toUpperCase()}</span>
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.97 }}
              transition={spring}
              className="absolute right-0 top-full mt-2 min-w-[140px] z-[9999]"
            >
              <div
                className="rounded-xl p-1.5"
                style={{
                  background: 'rgba(8, 18, 36, 0.94)',
                  backdropFilter: 'blur(32px) saturate(200%)',
                  WebkitBackdropFilter: 'blur(32px) saturate(200%)',
                  border: '1px solid rgba(100, 160, 200, 0.1)',
                  boxShadow: '0 20px 48px rgba(0,0,0,0.5)',
                }}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleSelect(lang.code)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors duration-150"
                    style={{
                      background: currentLang === lang.code ? 'rgba(80, 160, 220, 0.12)' : 'transparent',
                      color: currentLang === lang.code ? 'rgba(245, 250, 255, 1)' : 'rgba(200, 220, 240, 0.7)',
                      fontSize: '13px',
                      fontWeight: 500,
                    }}
                  >
                    <span className="text-base">{lang.flag}</span>
                    <span>{lang.label}</span>
                    {currentLang === lang.code && (
                      <span className="ml-auto text-[10px]" style={{ color: 'rgba(100, 200, 150, 0.8)' }}>
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // 默认样式：完整下拉
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-colors duration-200"
        style={{
          color: 'rgba(200, 220, 240, 0.7)',
          background: open ? 'rgba(100, 160, 200, 0.1)' : 'rgba(100, 160, 200, 0.04)',
          border: '1px solid rgba(100, 160, 200, 0.08)',
        }}
        title={t('language.switch')}
      >
        <Globe className="w-3.5 h-3.5" />
        <span>{currentLangInfo?.flag} {currentLangInfo?.label}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={spring}>
          <ChevronDown className="w-3 h-3 opacity-40" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={spring}
            className="absolute right-0 top-full mt-2 min-w-[180px] z-[9999]"
          >
            <div
              className="rounded-xl p-1.5"
              style={{
                background: 'rgba(8, 18, 36, 0.94)',
                backdropFilter: 'blur(32px) saturate(200%)',
                WebkitBackdropFilter: 'blur(32px) saturate(200%)',
                border: '1px solid rgba(100, 160, 200, 0.1)',
                boxShadow: '0 20px 48px rgba(0,0,0,0.5)',
              }}
            >
              <div className="px-3 py-2 mb-1">
                <div className="text-[11px] font-medium uppercase tracking-wider" style={{ color: 'rgba(158, 200, 224, 0.4)' }}>
                  {t('language.switch')}
                </div>
              </div>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleSelect(lang.code)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors duration-150"
                  style={{
                    background: currentLang === lang.code ? 'rgba(80, 160, 220, 0.12)' : 'transparent',
                    color: currentLang === lang.code ? 'rgba(245, 250, 255, 1)' : 'rgba(200, 220, 240, 0.7)',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                >
                  <span className="text-lg leading-none">{lang.flag}</span>
                  <span>{lang.label}</span>
                  {currentLang === lang.code && (
                    <span className="ml-auto text-[11px] font-medium" style={{ color: 'rgba(100, 200, 150, 0.9)' }}>
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
